import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { getEnvConfig, validateEnvConfig } from '../utils/env';
import type {
  Company,
  Industry,
  NewsItem,
  WatchlistItem,
  CompanySummary,
} from '../types';

// Get environment configuration
const envConfig = getEnvConfig();
const envValidation = validateEnvConfig(envConfig);

// Supabase client instance
let supabase: SupabaseClient | null = null;

// Initialize Supabase client with validation
if (envValidation.isValid && envConfig.VITE_SUPABASE_URL && envConfig.VITE_SUPABASE_ANON_KEY) {
  try {
    supabase = createClient(envConfig.VITE_SUPABASE_URL, envConfig.VITE_SUPABASE_ANON_KEY);
  } catch (error) {
    console.error('❌ Failed to initialize Supabase client:', error);
    throw new Error('Supabase 初始化失败，请检查环境配置');
  }
} else {
  console.error('❌ Supabase 配置缺失');
  if (!envValidation.isValid) {
    console.error('⚠️ 缺少环境变量:', envValidation.missingVars);
  }
  throw new Error('请配置 VITE_SUPABASE_URL 和 VITE_SUPABASE_ANON_KEY 环境变量');
}

// Export Supabase client
export { supabase };

// Auth API
export const auth = {
  async signUp(email: string, password: string) {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { data, error };
  },

  async signIn(email: string, password: string) {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  async signOut() {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async resendVerificationEmail(email: string) {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    // Supabase v2 中，重新发送验证邮件的方式是重新调用 signUp
    // 如果用户已存在但未验证，会重新发送验证邮件
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
    });
    return { data, error };
  },

  async resetPassword(email: string) {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}`,
    });
    return { data, error };
  },

  // Google OAuth 登录
  async signInWithGoogle() {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}`,
      },
    });
    return { data, error };
  },

  // 检查邮箱是否已注册并验证过
  async checkEmailExists(email: string) {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    
    try {
      // 尝试使用一个错误的密码登录来检查用户是否存在
      // 这是一个技巧：如果用户不存在，Supabase会返回特定错误
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password: 'invalid_password_for_checking',
      });

      if (error) {
        const errorMessage = error.message.toLowerCase();
        
        // 如果是密码错误，说明用户存在且已验证
        if (errorMessage.includes('invalid login credentials') || 
            errorMessage.includes('invalid password') ||
            errorMessage.includes('wrong password')) {
          return { exists: true, verified: true };
        }
        
        // 如果是邮箱未验证，说明用户存在但未验证
        if (errorMessage.includes('email not confirmed') || 
            errorMessage.includes('email_not_confirmed')) {
          return { exists: true, verified: false };
        }
        
        // 其他错误，假设用户不存在
        return { exists: false, verified: false };
      }
      
      // 如果没有错误，说明密码竟然对了（不太可能）
      // 立即退出登录并返回用户存在且已验证
      await this.signOut();
      return { exists: true, verified: true };
      
    } catch (err) {
      // 出现异常，保守处理，假设用户不存在
      console.warn('Error checking email existence:', err);
      return { exists: false, verified: false };
    }
  },
};

// Watchlist API
export const watchlistAPI = {
  async getUserWatchlist(userId: string): Promise<WatchlistItem[]> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase.from('user_watchlist').select('*').eq('user_id', userId);

    if (error) throw error;
    return data || [];
  },

  // 新的优化API：一次性获取用户完整的关注数据
  async getUserCompleteWatchlist(userId: string): Promise<{
    watchedCompanies: CompanyWithProgressiveData[];
    watchedIndustries: IndustryWithNews[];
    allCompanies: Company[];
    allIndustries: Industry[];
  }> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-watchlist`;
    
    const headers = {
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ user_id: userId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // 转换数据格式以匹配前端期望的类型
      return {
        watchedCompanies: data.watchedCompanies.map((company: Company & { newsCount?: number; keyPointsCount?: number }) => ({
          ...company,
          newsCount: company.newsCount || 0,
          keyPointsCount: company.keyPointsCount || 0,
          isLoadingMetrics: false,
        })),
        watchedIndustries: data.watchedIndustries,
        allCompanies: data.allCompanies,
        allIndustries: data.allIndustries,
      };
    } catch (error) {
      console.error('Error in getUserCompleteWatchlist:', error);
      throw error;
    }
  },

  async addToWatchlist(
    userId: string,
    itemType: 'company' | 'industry',
    itemId: string
  ): Promise<WatchlistItem[]> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase
      .from('user_watchlist')
      .insert([
        {
          user_id: userId,
          item_type: itemType,
          item_id: itemId,
        },
      ])
      .select();

    if (error) throw error;
    return data || [];
  },

  async removeFromWatchlist(
    userId: string,
    itemType: 'company' | 'industry',
    itemId: string
  ): Promise<void> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { error } = await supabase
      .from('user_watchlist')
      .delete()
      .eq('user_id', userId)
      .eq('item_type', itemType)
      .eq('item_id', itemId);

    if (error) throw error;
  },
};

// Companies API
export const companiesAPI = {
  async getCompanies(): Promise<Company[]> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // 获取热门公司 - 直接从数据库按关键要点排序获取前N个
  async getPopularCompanies(limit: number = 10): Promise<Company[]> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .gt('key_points_count', 0) // 只选择有关键要点的公司
      .order('key_points_count', { ascending: false })
      .order('current_article_count_24hrs', { ascending: false }) // 要点相同时按新闻数量排序
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  // 批量获取公司新闻数量 - 性能优化
  async getBatchCompanyNewsCount(companyIds: string[]): Promise<{ [companyId: string]: number }> {
    if (!supabase) {
      console.error('Supabase 客户端未初始化');
      return {};
    }

    try {
      // 批量获取公司信息
      const { data: companies, error: companiesError } = await supabase
        .from('companies')
        .select('id, name')
        .in('id', companyIds);

      if (companiesError || !companies) return {};

      // 为每个公司批量查询新闻数量
      const countPromises = companies.map(async company => {
        const { count, error } = await supabase
          .from('news_items')
          .select('*', { count: 'exact', head: true })
          .contains('companies', [company.name]);

        return {
          companyId: company.id,
          count: error ? 0 : count || 0,
        };
      });

      const results = await Promise.all(countPromises);
      const newsCountMap: { [companyId: string]: number } = {};

      results.forEach(({ companyId, count }) => {
        newsCountMap[companyId] = count;
      });

      return newsCountMap;
    } catch (error) {
      console.error('Error in getBatchCompanyNewsCount:', error);
      return {};
    }
  },

  async addCompany(name: string, sector: string = '其他'): Promise<Company> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase
      .from('companies')
      .insert([{ name, sector }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getCompanyNewsCount(companyId: string): Promise<number> {
    if (!supabase) {
      console.error('Supabase 客户端未初始化');
      return 0;
    }
    try {
      // First get the company name
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (companyError || !company) return 0;

      // Use contains operator to search in companies array
      const { count, error } = await supabase
        .from('news_items')
        .select('*', { count: 'exact', head: true })
        .contains('companies', [company.name]);

      if (error) {
        console.error('Error getting company news count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getCompanyNewsCount:', error);
      return 0;
    }
  },

  async getCompanyNews(companyId: string): Promise<NewsItem[]> {
    if (!supabase) {
      console.error('Supabase 客户端未初始化');
      return [];
    }
    try {
      // First get the company name
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (companyError || !company) return [];

      // Use contains operator to search in companies array
      const { data, error } = await supabase
        .from('news_items')
        .select('*')
        .contains('companies', [company.name])
        .order('published_at', { ascending: false });

      if (error) {
        console.error('Error getting company news:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCompanyNews:', error);
      return [];
    }
  },
};

// Industries API
export const industriesAPI = {
  async getIndustries(): Promise<Industry[]> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase
      .from('industries')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getIndustryById(industryId: string): Promise<Industry | null> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase
      .from('industries')
      .select('*')
      .eq('id', industryId)
      .single();

    if (error) throw error;
    return data;
  },

  async addIndustry(name: string, description: string = ''): Promise<Industry> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const { data, error } = await supabase
      .from('industries')
      .insert([{ name, description }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getIndustryNewsCount(industryId: string): Promise<number> {
    if (!supabase) {
      console.error('Supabase 客户端未初始化');
      return 0;
    }
    try {
      // First get the industry name
      const { data: industry, error: industryError } = await supabase
        .from('industries')
        .select('name')
        .eq('id', industryId)
        .single();

      if (industryError || !industry) return 0;

      // Use contains operator to search in industries array
      const { count, error } = await supabase
        .from('news_items')
        .select('*', { count: 'exact', head: true })
        .contains('industries', [industry.name]);

      if (error) {
        console.error('Error getting industry news count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getIndustryNewsCount:', error);
      return 0;
    }
  },

  async getIndustryNews(industryId: string): Promise<NewsItem[]> {
    if (!supabase) {
      console.error('Supabase 客户端未初始化');
      return [];
    }
    try {
      // First get the industry name
      const { data: industry, error: industryError } = await supabase
        .from('industries')
        .select('name')
        .eq('id', industryId)
        .single();

      if (industryError || !industry) return [];

      // Use contains operator to search in industries array
      const { data, error } = await supabase
        .from('news_items')
        .select('*')
        .contains('industries', [industry.name])
        .order('published_at', { ascending: false });

      if (error) {
        console.error('Error getting industry news:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getIndustryNews:', error);
      return [];
    }
  },
};

// News API
export const newsAPI = {
  async getCompanySummary(companyId: string): Promise<CompanySummary> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');

    try {
      // 从companies表的summary_24hrs字段获取数据
      const { data: company, error } = await supabase
        .from('companies')
        .select('id, name, sector, summary_24hrs, updated_at')
        .eq('id', companyId)
        .single();

      if (error) {
        console.error('Error fetching company summary:', error);
        throw new Error(error.message);
      }

      if (!company) {
        throw new Error('Company not found');
      }

      if (!company.summary_24hrs) {
        // 如果没有24小时摘要，返回空的格式兼容结构
        return {
          id: companyId,
          companyName: company.name,
          sector: company.sector,
          summary: JSON.stringify([{ output: [] }]),
          sentiment: 'neutral' as const,
          newsCount: 0,
          lastUpdated: new Date().toISOString(),
        };
      }

      // 解析最新格式的摘要数据
      let summaryData: unknown;
      try {
        summaryData =
          typeof company.summary_24hrs === 'string'
            ? JSON.parse(company.summary_24hrs)
            : company.summary_24hrs;
      } catch (parseError) {
        console.error('Error parsing summary_24hrs:', parseError);
        throw new Error('Invalid summary data format');
      }

      return {
        id: companyId,
        companyName: company.name,
        sector: company.sector,
        summary: JSON.stringify(summaryData), // 直接返回最新格式的数据
        sentiment: 'neutral' as const,
        newsCount: summaryData.sources && Array.isArray(summaryData.sources) ? summaryData.sources.length : 0,
        lastUpdated: company.updated_at || new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error in getCompanySummary:', error);
      throw error;
    }
  },

  // 批量获取公司总结 - 性能优化
  async getBatchCompanySummary(
    companyIds: string[]
  ): Promise<{ [companyId: string]: CompanySummary }> {
    if (!supabase) throw new Error('Supabase 客户端未初始化');

    try {
      // 批量获取公司数据
      const { data: companies, error } = await supabase
        .from('companies')
        .select('id, name, sector, summary_24hrs, updated_at')
        .in('id', companyIds);

      if (error) {
        console.error('Error fetching batch company summaries:', error);
        throw new Error(error.message);
      }

      const result: { [companyId: string]: CompanySummary } = {};

      companies?.forEach(company => {
        let summaryData: unknown;

        if (!company.summary_24hrs) {
          summaryData = [{ output: [] }];
        } else {
          try {
            summaryData =
              typeof company.summary_24hrs === 'string'
                ? JSON.parse(company.summary_24hrs)
                : company.summary_24hrs;
          } catch (parseError) {
            console.error('Error parsing summary_24hrs for company', company.id, parseError);
            summaryData = [{ output: [] }];
          }
        }

        result[company.id] = {
          id: company.id,
          companyName: company.name,
          sector: company.sector,
          summary: JSON.stringify(summaryData),
          sentiment: 'neutral' as const,
          newsCount: summaryData.sources && Array.isArray(summaryData.sources) ? summaryData.sources.length : 0,
          lastUpdated: company.updated_at || new Date().toISOString(),
        };
      });

      return result;
    } catch (error) {
      console.error('Error in getBatchCompanySummary:', error);
      throw error;
    }
  },

  async getCitationContent(blobID: string, rowId: string, fromLine: number, toLine: number) {
    if (!supabase) throw new Error('Supabase 客户端未初始化');
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-citation-content`;

    const headers = {
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        blobID,
        rowId,
        fromLine,
        toLine,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  },
};
